/**
 * SwipeableLayout - Snapchat-style horizontal swipe navigation.
 * Three full-viewport panels with CSS scroll-snap for native feel.
 * Remembers last panel when navigating away and restores on return.
 */
import { useRef, useEffect, useLayoutEffect, useCallback, lazy, Suspense, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useNavigationStore, type MainPanel } from '../store/navigationStore';
import { useCameraStore } from '../store/cameraStore';
import { useWebSocket, useDarkCanvas } from '../hooks';
import FirstTimeSetupModal, { hasSeenSetup } from '../organisms/FirstTimeSetupModal';

const PANEL_STORAGE_KEY = 'momento_active_panel';

// Lazy load section organisms
const ChatSection = lazy(() => import('../organisms/ChatSection'));
const CameraSection = lazy(() => import('../organisms/CameraSection'));
const FaithSection = lazy(() => import('../organisms/FaithSection'));

// Loading fallback for sections
function SectionLoader() {
  return (
    <div className="flex h-full items-center justify-center bg-background">
      <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent" />
    </div>
  );
}

const panelToIndex: Record<MainPanel, number> = {
  chat: 0,
  camera: 1,
  faith: 2,
};

/**
 * A `?panel=` on the home route is a notification's landing request
 * (FEAT-040): a moment push opens the CHATS LIST, never the thread —
 * "I just wanted it to open up the chat panel." Read once, then stripped.
 */
function requestedPanel(search: string): MainPanel | null {
  const panel = new URLSearchParams(search).get('panel');
  return panel === 'chat' || panel === 'camera' || panel === 'faith' ? panel : null;
}

export default function SwipeableLayout() {
  const containerRef = useRef<HTMLDivElement>(null);
  const location = useLocation();
  const navigate = useNavigate();
  const { activePanelIndex, setActivePanelIndex, isImmersive } = useNavigationStore();
  const { clearReplyContext, currentCapture, initStream } = useCameraStore();
  const [showSetupModal, setShowSetupModal] = useState(() => !hasSeenSetup());
  const prevPanelRef = useRef(activePanelIndex);

  // Panels don't scroll while recording, or under the setup sheet (BUG-020)
  const panelsLocked = isImmersive || showSetupModal;

  // Connect to WebSocket for real-time updates
  useWebSocket();

  // Camera panel: dye the canvas (home-indicator strip) black to match
  useDarkCanvas(activePanelIndex === 1);

  // Scroll to panel when activePanelIndex changes programmatically
  const scrollToPanel = useCallback((index: number) => {
    if (containerRef.current) {
      const panelWidth = containerRef.current.offsetWidth;
      containerRef.current.scrollTo({
        left: index * panelWidth,
        behavior: 'smooth',
      });
    }
  }, []);

  // Initialize scroll position - restore last panel or default to camera.
  // useLayoutEffect, so the position is set before the first paint rather than
  // flashing the chat panel first.
  useLayoutEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    // A notification's landing request outranks the remembered panel; then
    // try to restore last panel from storage.
    const requested = requestedPanel(window.location.search);
    const savedPanel = sessionStorage.getItem(PANEL_STORAGE_KEY);
    const targetIndex = requested
      ? panelToIndex[requested]
      : savedPanel !== null
        ? parseInt(savedPanel, 10)
        : 1; // Default to camera

    // Scroll instantly (no animation) to the panel
    const applyScroll = () => {
      container.scrollLeft = targetIndex * container.offsetWidth;
    };
    applyScroll();

    // Guard the landing: this is a mandatory scroll-snap container whose
    // panels are lazy-loaded, and the browser re-snaps to the first panel
    // whenever those sections finish laying out. One rAF re-assert wasn't
    // enough — a chunk landing later than one frame still flashed the Chat
    // panel over the Camera on launch (BUG-052). So for a short window, undo
    // any scroll we didn't cause; the first real touch ends the guard early.
    const onScroll = () => applyScroll();
    container.addEventListener('scroll', onScroll);
    const endGuard = () => {
      container.removeEventListener('scroll', onScroll);
      container.removeEventListener('touchstart', endGuard);
    };
    container.addEventListener('touchstart', endGuard);
    const guardTimer = setTimeout(endGuard, 600);

    // Update store to match — ALWAYS, including the default (BUG-021).
    // This used to be gated on savedPanel !== null, so on a cold launch the
    // store said panel 0 (chat) while the screen showed panel 1 (camera):
    // the canvas stayed cream behind a black viewfinder, and CameraSection's
    // init effect (keyed on activePanel === 'camera') didn't fire until a
    // scroll event eventually corrected the store.
    setActivePanelIndex(targetIndex);

    // Landing on the camera? Start it from here, not from CameraSection —
    // that organism is lazy-loaded, so waiting for its chunk to download and
    // mount before even asking for the camera cost a visible beat (BUG-022).
    // This runs in parallel with the chunk instead.
    if (targetIndex === 1) {
      void initStream();
    }

    return () => {
      clearTimeout(guardTimer);
      endGuard();
    };
  }, [setActivePanelIndex, initStream]);

  // Notification landings while the layout is ALREADY mounted (a push tapped
  // with the app open on the home screen): honour `?panel=` + `?tab=groups`,
  // then strip the query so a refresh doesn't replay it. The mount effect
  // above covers the cold path; this smooth-scrolls the warm one.
  useEffect(() => {
    const requested = requestedPanel(location.search);
    if (!requested) return;
    if (new URLSearchParams(location.search).get('tab') === 'groups') {
      useNavigationStore.getState().requestChatTab('groups');
    }
    scrollToPanel(panelToIndex[requested]);
    setActivePanelIndex(panelToIndex[requested]);
    navigate('/', { replace: true });
  }, [location.search, navigate, scrollToPanel, setActivePanelIndex]);

  // Save panel to storage when it CHANGES — never on the first run.
  // The first run always carries the store's stale default (0 = chat) because
  // the restore effect's update hasn't landed yet, and writing that poisoned
  // the restore: ProtectedRoute unmounts this layout while checkAuth() runs on
  // launch and then remounts it, and the remount read back that bogus 0 and
  // dropped us on Chat instead of the Camera (BUG-021).
  const panelSaved = useRef(false);
  useEffect(() => {
    if (!panelSaved.current) {
      panelSaved.current = true;
      return;
    }
    sessionStorage.setItem(PANEL_STORAGE_KEY, activePanelIndex.toString());
  }, [activePanelIndex]);

  // Clear reply context when leaving camera panel without a capture
  // This prevents stale reply context from showing when navigating away
  useEffect(() => {
    const wasOnCamera = prevPanelRef.current === 1;
    const leftCamera = activePanelIndex !== 1;

    // If we left the camera panel without having taken a capture, clear reply context
    if (wasOnCamera && leftCamera && !currentCapture) {
      clearReplyContext();
    }

    prevPanelRef.current = activePanelIndex;
  }, [activePanelIndex, currentCapture, clearReplyContext]);

  // Listen for nav button clicks to scroll to panel
  useEffect(() => {
    const handleNavChange = (event: CustomEvent<{ panel: MainPanel }>) => {
      const index = panelToIndex[event.detail.panel];
      scrollToPanel(index);
    };

    window.addEventListener('nav-panel-change', handleNavChange as EventListener);
    return () => {
      window.removeEventListener('nav-panel-change', handleNavChange as EventListener);
    };
  }, [scrollToPanel]);

  // The width every scroll offset in here is denominated in. Owned by the
  // observer below; read by the scroll handler to tell a swipe apart from the
  // browser moving the paper under the person.
  const panelWidthRef = useRef(0);

  // Track scroll position to update active panel
  const handleScroll = useCallback(() => {
    if (containerRef.current) {
      const { scrollLeft, offsetWidth } = containerRef.current;

      // scrollLeft defines the panel only at the width the person actually
      // scrolled at. Mid-rotation the browser both resizes the container and
      // moves the offset itself — a mandatory-snap re-snap, or a CLAMP when the
      // container narrows and the old offset is past the new maximum — and each
      // of those emits a scroll event that looks exactly like a swipe. It is
      // not one, and reading an index out of it is how the camera turned into
      // the Bible: pinned, max scroll is 1170−390 = 780, so a re-asserted 844
      // clamps to 780, which rounds to panel 2 (BUG-081 round 2). Skip anything
      // that arrives at a width the observer hasn't accepted yet; it will
      // re-anchor us a moment later from an index this never corrupted.
      if (offsetWidth !== panelWidthRef.current) return;

      const newIndex = Math.round(scrollLeft / offsetWidth);

      if (newIndex !== activePanelIndex && newIndex >= 0 && newIndex <= 2) {
        setActivePanelIndex(newIndex);
      }
    }
  }, [activePanelIndex, setActivePanelIndex]);

  // Re-anchor the active panel whenever the container's WIDTH changes (BUG-081).
  //
  // The panel you are on is stored as a scroll offset — scrollLeft measured in
  // panel widths — so a width change silently redefines it: at 390px wide,
  // scrollLeft 390 is the camera; the instant the container becomes 844px wide
  // that same 390 rounds to panel 0 and mandatory snap drags you to Chat. The
  // panels are sized off the frame now (w-full, never w-screen — viewport units
  // don't know the portrait frame exists), so a rotation shouldn't change this
  // width at all; but the pin drops for a frame on the way round (BUG-083) and
  // a desktop window resizes freely, and neither is allowed to move you.
  //
  // Read the index imperatively: this effect is set up once, so a closed-over
  // activePanelIndex would be frozen at whatever it was on mount. The index is
  // trustworthy because the scroll handler above refuses to write one from a
  // scroll that arrived at a width this observer hadn't accepted yet.
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    // Seeded here as well as from the observer's first delivery: until the ref
    // holds a real width the scroll handler treats every scroll as geometry,
    // and a pager that ignores swipes is a worse bug than the one being fixed.
    panelWidthRef.current = container.offsetWidth;

    const observer = new ResizeObserver(() => {
      const next = container.offsetWidth;
      if (!next || next === panelWidthRef.current) return;
      panelWidthRef.current = next;
      container.scrollLeft = useNavigationStore.getState().activePanelIndex * next;
    });
    observer.observe(container);
    return () => observer.disconnect();
  }, []);

  // Frame-throttled scroll handler (BUG-021). This was a 50ms debounce whose
  // timer RESET on every scroll event, so it couldn't fire until scrolling
  // fully stopped — iOS momentum included. Anything keyed on the active panel
  // (the black camera canvas, the nav highlight) repainted long after the
  // panel had visually landed. rAF flips it the frame the panel crosses the
  // halfway point, which reads as instant.
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    let frame = 0;

    const onScroll = () => {
      if (frame) return;
      frame = requestAnimationFrame(() => {
        frame = 0;
        handleScroll();
      });
    };

    container.addEventListener('scroll', onScroll, { passive: true });

    return () => {
      container.removeEventListener('scroll', onScroll);
      if (frame) cancelAnimationFrame(frame);
    };
  }, [handleScroll]);

  return (
    // w-full, never w-screen: `100vw` is the WINDOW's width, and sideways the
    // window is the long edge while the app lives in a portrait box turned into
    // it (FEAT-023) — 844px of panel inside a 390px frame that clips it. The
    // height family is already remapped to the frame in tailwind.config.js; the
    // width family has no such hook, so it has to come from the parent chain.
    <div
      className="relative w-full overflow-hidden bg-background"
      style={{ height: 'var(--viewport-height)' }}
    >
      {/* Horizontal scroll container - locked during immersive mode (recording)
          and while the setup sheet is up, so the panels can't scroll behind it */}
      <div
        ref={containerRef}
        className={`
          flex h-full w-full
          overflow-y-hidden
          scrollbar-hide
          ${panelsLocked ? 'overflow-x-hidden' : 'overflow-x-auto snap-x snap-mandatory'}
        `}
        style={{
          scrollSnapType: panelsLocked ? 'none' : 'x mandatory',
          WebkitOverflowScrolling: panelsLocked ? 'auto' : 'touch',
          touchAction: panelsLocked ? 'none' : 'pan-x pan-y',
        }}
      >
        {/* Chat Panel (Left) */}
        <section
          className="
            flex-shrink-0 w-full h-full
            snap-center snap-always
          "
          aria-label="Chat"
        >
          <Suspense fallback={<SectionLoader />}>
            <ChatSection />
          </Suspense>
        </section>

        {/* Camera Panel (Center) */}
        <section
          className="
            flex-shrink-0 w-full h-full
            snap-center snap-always
          "
          aria-label="Camera"
        >
          <Suspense fallback={<SectionLoader />}>
            <CameraSection />
          </Suspense>
        </section>

        {/* Faith Panel (Right) */}
        <section
          className="
            flex-shrink-0 w-full h-full
            snap-center snap-always
          "
          aria-label="Faith"
        >
          <Suspense fallback={<SectionLoader />}>
            <FaithSection />
          </Suspense>
        </section>
      </div>

      {/* First-time setup modal */}
      {showSetupModal && (
        <FirstTimeSetupModal onComplete={() => setShowSetupModal(false)} />
      )}
    </div>
  );
}
